import _ from "lodash";

import GenericCacheStore from "#/data-layer/GenericCacheStore";
import GenericOfflineStore from "#/data-layer/GenericOfflineStore";
import GenericPokkService from "#/data-layer/GenericPokkService";
import { DEFINITIONS } from "#/def/data-layer";
import useConnectionState from "#/hooks/useConnectionState.js";

import useMutationPromiseHandler from "./useMutationPromiseHandler";

const list2obj = (list) =>
  list.reduce((acc, item) => ({ ...acc, [item]: item }), {});

const Mode = list2obj([
  // default mode
  // Use GenericPokkService directly, do not cache
  // anything
  "connected",

  // Use GenericOfflineStore for caching queries
  // Send payloads to POKK service imediately
  // and update mutated items in local cache.
  //
  // Do not mark items as dirty on mutations
  "cache",

  // Use GenericOfflineStore
  // use only offline-store for queries, reads
  // and mutations
  "offline",
]);

export const Mutation = list2obj(["create", "update", "delete"]);

// maps to hold store instances for each ident and mode, to ensure we reuse the same instance across the app
const storeMap = new Map();

/**
 * Initializes the appropriate data layer class based on the provided parameters and mode.
 *
 * The function determines which type of data layer to use (connected, cache, or offline) and
 * initializes it with the necessary parameters such as the identifier, API base URL,
 * IndexedDB instance, reload function, and online status function.
 *
 * It takes care of lookup-tables exceptions by adjusting the
 * identifier and applying specific mappers and filters to ensure
 *
 * @param {string} ident - The identifier for the data layer, used to determine which store instance to use or create.
 * @param {string} mode - The mode of the data layer, can be 'connected', 'cache', or 'offline'. Determines which type of data layer to use and how it handles caching and offline support.
 * @param {object} params - Additional parameters for configuring the data layer instance, such as API base URL, reload function, online status function, etc.
 * @param {function} isOnline - A function that returns the current online status, used to determine whether to use cached data or query the service directly.
 * @returns The initialized data layer instance.
 */
function dataLayerFactory(ident, mode, $params, isOnline) {
  if (!ident) {
    throw new Error(
      "Identifier is required to initialize data layer. Please provide a valid identifier in the parameters."
    );
  }

  if (!(mode in Mode)) {
    throw new Error(`Unknown mode: ${mode}`);
  }

  const isLookupTable = ident.startsWith("sif/");

  const defaultDataLayer = new GenericPokkService({
    ...$params.service,
  });
  const dataLayer = $params.dataLayer ?? defaultDataLayer;

  delete $params.service;

  if (mode === Mode.connected) {
    return dataLayer;
  }

  // general use params
  const params = {
    // spread the provided parameters to allow for additional configuration
    ...$params,

    // make sure that the ident always set correctly
    ident,

    // Use explicit custom data layer when provided, otherwise fall back
    // to the generic service derived from the ident.
    dataLayer,

    // function to determine if we are currently offline,
    // based on the provided isOnline function or the online status hook
    isOnline,
  };

  if (isLookupTable) {
    // When dealing with "sif/" data layers, we want to use the same cache entries for all of them,
    // since they are all just different "views" of the same underlying data.
    // To achieve that, we set the ident to a common value for all "sif/" data layers.
    //
    // Clearly, this is a bit of a hack and not the most elegant solution,
    // but it allows us to have separate service instances for each "sif/" data
    // layer (to handle different API endpoints and mappers/filters),
    params.ident = `lookup-table`;
    params.systemMapper = (item) => ({ ...item, $ident: ident });
    params.systemFilter = (item) => item.$ident === ident;
  }

  // eslint-disable-next-line default-case
  switch (params.mode) {
    case Mode.cache:
      return new GenericCacheStore(
        // merge the general params with the cache-specific params if provided,
        params.cache ? { ...params, ...params.cache } : params
      );

    case Mode.offline:
      return new GenericOfflineStore(
        // merge the general params with the offline-specific params if provided,
        params.offline ? { ...params, ...params.offline } : params
      );
  }
}

/**
 *
 * @param {string} ident
 * @param {string} mode
 * @param {object} param
 * @param {function|boolean} isOnline
 * @returns
 */
function getDataLayer(
  ident,
  mode,
  {
    //use <ident>:<mode> as store key to ensure we have a unique
    // store instance for each ident and mode combination
    // there are special cases where want to enforce unique
    // instance or avoid using storeMap altogether
    storeMapKey = `${ident}:${mode}`,
    ...params
  },
  isOnline
) {
  if (storeMapKey === false || !storeMap.has(storeMapKey)) {
    const dataLayer = dataLayerFactory(ident, mode, params, isOnline);
    if (storeMapKey === false) {
      return dataLayer;
    }

    // we do not have a store instance for this ident and mode yet,
    // let's create one and store it in the map
    storeMap.set(storeMapKey, dataLayer);

    // eslint-disable-next-line no-console
    console.debug(
      `useDataLayer: Created new data layer instance for ident "${ident}" in "${mode}" mode (isOnline: ${isOnline()})`
    );
  }

  return storeMap.get(storeMapKey);
}

/**
 * Internal custom hook to handle dirty state in/with IndexedDB.
 *
 * This is more of a utility hook to be used by the other data layer hooks,
 * direct use of this hook is not recommended or supported
 *
 * If not configured otherwise, function will initialize a new GenericOfflineStore
 * instance with the provided identifier and use the useConnectionState
 * hook to determine online/offline status.
 *
 * @param {object|string} params - Options for the hook, can be a string representing the identifier of the IndexedDB store or an object with more detailed parameters.
 * @param {string} params.ident - Identifier (name) of the IndexedDB object store to use.
 * @param {IndexedDB} params.idb - IndexedDB instance to interact with the local store
 * @param {function} params.reload - Reload function to call after performing operations that modify the local store, to allow the UI to refresh with the latest data.
 * @param {function|boolean} params.isOnline - Optional function to determine online status, if not provided the hook will use the useOnlineStatus hook to determine online/offline status.
 * @param {string} params.mode - Optional flag to off/online behavior and cache, can be 'connected', 'cache', or 'offline'. Determines which type of data layer to use and how it handles caching and offline support.
 *
 * @return {object} - An object containing the IndexedDB instance, online status function, reload function, and any additional parameters.
 * @return {function} return.dataLayer - The initialized data layer instance for interacting with the local IndexedDB store.
 * @return {function} return.isOnline - A function that returns the current online status, based on the provided isOnline parameter or the useOnlineStatus hook.
 * @return {function} return.reload - A function that can be called to trigger a reload of the data, typically after performing operations that modify the local store.
 *
 * @throws Will throw an error if the required "identifier" parameter is not provided, or if the provided dataLayer instance is not an instance of GenericOfflineStore.
 */
function init($params, connectionState) {
  if ($params == null) {
    return {
      reload: () => Promise.resolve(),
      dataLayer: null,
    };
  }

  let params = {};
  if (typeof $params === "string") {
    if (!DEFINITIONS.has($params)) {
      throw new Error(
        `No data layer definition found for identifier "${$params}". Please provide a valid identifier or a full configuration object.`
      );
    }

    params = DEFINITIONS.get($params);
  } else if (typeof $params === "object") {
    params = _.merge({}, DEFINITIONS.get($params.ident), $params);
  } else {
    throw new Error(
      "Invalid parameter provided to useDataLayerInit. Expected a string identifier or a configuration object."
    );
  }

  if (!params?.mode) {
    // always default to connected  mode,
    // since that is the most common use case and it provides the best
    // user experience with automatic caching and offline support
    params.mode = Mode.connected;
  }

  // function to determine if we are currently offline,
  // based on the provided isOnline function or the online status hook
  const isOnline = () => {
    switch (true) {
      case typeof params.isOnline === "function":
        return params.isOnline();
      case typeof params.isOnline === "boolean":
        return params.isOnline;
      default:
        return connectionState.isOnline;
    }
  };

  return {
    // no-op if not provided,
    // to avoid having to check for existence before calling
    reload: () => Promise.resolve(),

    ...params,

    // enforce proper data layer initialization
    dataLayer: getDataLayer(params.ident, params.mode, params, isOnline),
  };
}

export function useDataLayer($params) {
  const { dataLayer } = init($params, useConnectionState());
  return dataLayer;
}

/**
 * Data Layer mutation hook with update/delete/create capabilities
 *
 * @param {object|string} params - Options for the hook, can be a string representing the identifier of the IndexedDB store or an object with more detailed parameters.
 * @param {string} params.ident - Identifier (name) of the IndexedDB object store to use.
 * @param {string} params.apiBaseUrl - An optional base URL for API calls, if needed for synchronization. Derived from the identifier when not provided
 * @param {IndexedDB} params.idb - IndexedDB instance to interact with the local store
 * @param {function} params.reload - Reload function to call after performing operations that modify the local store, to allow the UI to refresh with the latest data.
 * @param {string} params.mode - Optional flag to off/online behavior and cache, can be 'connected', 'cache', or 'offline'. Determines which type of data layer to use and how it handles caching and offline support.
 *
 * @returns {object} - An object containing the IndexedDB instance, online status function, reload function, and any additional parameters.
 * @returns {function} return.create - A function that takes an item object and creates a new entry in the data layer, with offline support.
 * @returns {function} return.update - A function that takes an ID and an item object, and updates the corresponding entry in the data layer, with offline support.
 * @returns {function} return.delete - A function that takes an ID and deletes the corresponding entry from the data layer, with offline support.
 * @returns {function} return.sync - A function that triggers synchronization of the local IndexedDB store with the remote service when the application is back online.
 */
export function useDataLayerMutation($params) {
  const params = init($params, useConnectionState());

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [outcome, setOutcome] = useState(null);

  // common handler for create/update/delete mutations,
  // to handle loading and error state in a consistent way
  const mutationPromiseHandler = (promise) => {
    setIsLoading(true);
    setError(null);

    return promise
      .then(($result) => {
        setOutcome($result);
        params.reload();
        return $result;
      })
      .catch((err) => {
        setError(err);
        throw err;
      })
      .finally(() => setIsLoading(false));
  };

  return {
    ...params,

    outcome,
    isLoading,
    error,

    create: async (item) => {
      return mutationPromiseHandler(
        // create a new item in the store with the provided data
        params.dataLayer.create(item)
      );
    },

    update: async (id, item) => {
      return mutationPromiseHandler(
        // update by id with new item data
        params.dataLayer.update(id, item)
      );
    },

    delete: async (id, version) => {
      return mutationPromiseHandler(
        // delete an item from the store by id and version
        params.dataLayer.delete(id, version)
      );
    },

    sync: async (...items) => {
      return params.dataLayer.sync(...items).then(params.reload);
    },
  };
}

/**
 * Data Layer mutation get hook for retriving a single resource based on ID parameter,
 *
 * @param {object|string} params - Options for the hook, can be a string representing the identifier of the IndexedDB store or an object with more detailed parameters.
 * @param {string} params.ident - Identifier (name) of the IndexedDB object store to use.
 * @param {string} params.apiBaseUrl - An optional base URL for API calls, if needed for synchronization. Derived from the identifier when not provided
 * @param {IndexedDB} params.idb - IndexedDB instance to interact with the local store
 * @param {function} params.reload - Reload function to call after performing operations that modify the local store, to allow the UI to refresh with the latest data.
 * @param {string} params.mode - Optional flag to off/online behavior and cache, can be 'connected', 'cache', or 'offline'. Determines which type of data layer to use and how it handles caching and offline support.
 *
 * @returns {object} - An object containing the IndexedDB instance, online status function, reload function, and any additional parameters.
 * @returns {object.item} return.item - The retrieved item based on the provided ID parameter.
 * @returns {boolean} return.isLoading - A boolean indicating whether the data retrieval is currently in progress.
 * @returns {object} return.error - An object containing error information if the data retrieval fails, or null if there is no error.
 * @returns {function} return.read - A function that takes an ID parameter and triggers the data retrieval process for that specific ID.
 */
export function useDataLayerRead($params) {
  const params = init($params, useConnectionState());
  const { dataLayer, reload: reloadParent } = params;
  const reloadParentRef = useRef(reloadParent);

  // Keep the current id in a ref so the read callback stays stable across renders.
  const idRef = useRef($params.id || null);

  const [item, setItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const clear = useCallback(() => {
    setIsLoading(false);
    setItem(null);
    setError(null);
  }, []);

  // function to refresh the results based on the current query parameters
  const reload = useCallback(
    async ($id = idRef.current) => {
      setIsLoading(true);
      setError(null);
      setItem(null);

      if (!$id) {
        clear();
        return;
      }

      return dataLayer
        .read($id)
        .then((res) => {
          setItem(res);
          reloadParentRef.current?.();
          return res;
        })
        .catch((err) => {
          setError(err);
          throw err;
        })
        .finally(() => setIsLoading(false));
    },
    [clear, dataLayer]
  );

  const read = useCallback(
    async ($id) => {
      idRef.current = $id;
      return reload($id);
    },
    [reload]
  );

  return {
    ...params,

    item,
    isLoading,
    error,

    reload,

    clear,

    read,
  };
}

/**
 * Data Layer query hook for fetching a list of items based on query parameters,
 *
 * @param {object|string} params - Options for the hook, can be a string representing the identifier of the IndexedDB store or an object with more detailed parameters.
 * @param {string} params.ident - Identifier (name) of the IndexedDB object store to use.
 * @param {string} params.apiBaseUrl - An optional base URL for API calls, if needed for synchronization. Derived from the identifier when not provided
 * @param {IndexedDB} params.idb - IndexedDB instance to interact with the local store
 * @param {function} params.reload - Reload function to call after performing operations that modify the local store, to allow the UI to refresh with the latest data.
 * @param {string} params.mode - Optional flag to off/online behavior and cache, can be 'connected', 'cache', or 'offline'. Determines which type of data layer to use and how it handles caching and offline
 * @param {number} params.skeleton.pageSize - Construct a skeleton result with the specified number of items to show loading state in the UI while the actual query is in progress.
 *
 * @return {object} - An object containing the IndexedDB instance, online status function, reload function, and any additional parameters.
 * @return {object} return.result - The result of the query, typically an object containing an array of items and metadata such as total count, page number, etc.
 * @return {boolean} return.isLoading - A boolean indicating whether the query is currently in progress.
 * @return {object} return.error - An object containing error information if the query fails, or null if there is no error.
 * @return {function} return.query - A function that takes query parameters and triggers the data retrieval process based on those parameters.
 */
export function useDataLayerQuery($params) {
  const params = init($params, useConnectionState());
  const { dataLayer, reload: reloadParent } = params;
  const reloadParentRef = useRef(reloadParent);

  const queryParamsRef = useRef({});
  const empty = useMemo(
    () =>
      Object.freeze({
        content: params.skeleton
          ? // if skeleton page size is provided, we return an array of empty items to show
            // loading state in the UI while the actual query is in progress
            Array.from({ length: params.skeleton?.pageSize || 0 }).map(
              (_data, index) => ({
                id: `loading-${index}`,
              })
            )
          : // if no skeleton page size is provided, we return an empty array
            [],
        total: 0,
        pageNo: undefined,
        pageSize: undefined,
      }),
    []
  );

  // if skeleton page size is provided, we assume that
  // the query is already in progress and we want to show the
  const [isLoading, setIsLoading] = useState(!!$params?.skeleton?.pageSize);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(empty);

  // function to reload the results based on the current query parameters
  const reload = useCallback(
    async ($queryParams = queryParamsRef.current) => {
      queryParamsRef.current = $queryParams || {};

      setIsLoading(true);
      setResult(empty);
      setError(null);

      return dataLayer
        .query({ ...$queryParams })
        .then((res) => {
          setResult(res);
          reloadParentRef.current?.();

          return res;
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error(err);

          setError(err.message);

          throw err;
        })
        .finally(() => setIsLoading(false));
    },
    [dataLayer, empty]
  );

  const query = useCallback(
    async (qp) => {
      queryParamsRef.current = qp || {};
      return reload(qp);
    },
    [reload]
  );

  return {
    ...params,

    isLoading,
    error,
    result,

    reload,
    query,
  };
}

/**
 * Generic form hook for handling create/update/delete mutations with a data layer
 *
 * It handles form submission for both creating new items and updating existing items, as well as deleting items.
 * It uses the useDataLayerMutation hook to perform the actual mutations and a mutation promise
 * handler to manage loading and error states.
 *
 * @param {object} $params - Parameters for the data layer mutation hook.
 * @param {function|string} $params.redirectTo - A URL to navigate back to after a successful mutation, used in the mutation promise handler for navigation.
 * @returns {object} - An object containing mutation functions and handlers for form operations.
 */
export function useDataLayerMutationFormHandlers({ redirectTo, replace, ...$params }) {
  const mutation = useDataLayerMutation($params);

  // handles the promise returned by the mutation functions to
  // notify on success or failure of the mutation
  // as well as navigation after successful mutations
  const handleMutationPromise = useMutationPromiseHandler({ redirectTo, replace });

  const create = async (data) => {
    return handleMutationPromise(mutation.create(data), Mutation.create);
  };

  const update = async (id, data) => {
    return handleMutationPromise(mutation.update(id, data), Mutation.update);
  };

  return {
    isLoading: mutation.isLoading,
    error: mutation.error,

    // handles form submission for both creating new items and
    // updating existing items based on the presence of an ID in the data
    submit: async (data) => {
      if (data.version !== undefined) {
        return update(data.id, data);
      } else {
        return create(data);
      }
    },

    // handles creation of a new item with the provided data
    create,

    // handles update of an item by id with new data
    update,

    // handles deletion of an item by id and version
    delete: async (id, version) => {
      return handleMutationPromise(
        mutation.delete(id, version),
        Mutation.delete
      );
    },
  };
}

/**
 * Handles uploading of files and attachments with the data layer, providing loading and error state management.
 *
 * @param {object|string} params - Options for the hook, can be a string representing the identifier of the IndexedDB store or an object with more detailed parameters.
 * @param {string} params.ident - Identifier (name) of the IndexedDB object store to use.
 * @param {string} params.apiBaseUrl - An optional base URL for API calls, if needed for synchronization. Derived from the identifier when not provided
 * @param {IndexedDB} params.idb - IndexedDB instance to interact with the local store
 * @param {function} params.reload - Reload function to call after performing operations that modify the local store, to allow the UI to refresh with the latest data.
 * @param {string} params.mode - Optional flag to off/online behavior and cache, can be 'connected', 'cache', or 'offline'. Determines which type of data layer to use and how it handles caching and offline support.
 *
 * @returns {object} - An object containing the IndexedDB instance, online status function, reload function, and any additional parameters.
 * @returns {function} return.create - A function that takes an item object and creates a new entry in the data layer, with offline support.
 * @returns {function} return.update - A function that takes an ID and an item object, and updates the corresponding entry in the data layer, with offline support.
 * @returns {function} return.delete - A function that takes an ID and deletes the corresponding entry from the data layer, with offline support.
 * @returns {function} return.sync - A function that triggers synchronization of the local IndexedDB store with the remote service when the application is back online.
 */
export function useDataLayerUpload($params) {
  const params = init($params, useConnectionState());

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [outcome, setOutcome] = useState(null);

  return {
    ...params,

    outcome,
    isLoading,
    error,

    /**
     * Uploads (and attaches) a file
     *
     * If ref is provided, the uploaded file will be attached to the item
     * with the provided ID, otherwise it will just be uploaded without attachment.
     *
     * We are more opinionated about the data structure here, since the file metadata
     * is usually expected to have a certain format, but we can adjust that as needed
     * based on the actual requirements and the structure of the data layer we are working with.
     *
     * @param {Blob} blob - The file data to be uploaded, typically represented as a Blob object.
     * @param {object} meta - An object containing metadata about the file being uploaded, such as filename, content type, size, etc. This metadata can be used to provide additional context about the file and may be stored alongside the file in the data layer.
     * @param {string} meta.filename - The name of the file being uploaded, used for identification and display purposes.
     * @param {string} [ref] - Optional ID of the item to which the uploaded file should be attached, if applicable
     * @returns {object} The response from the upload operation, typically containing information about the uploaded file and any relevant metadata
     */
    upload: async (blob, meta = {}, ref = null) => {
      setIsLoading(true);
      setError(null);
      setOutcome(null);

      return params.dataLayer
        .upload(blob, meta)
        .then((out) => {
          setOutcome(out);
          params.reload();
          return out;
        })
        .catch((err) => {
          setError(err);
          throw err;
        })
        .finally(() => setIsLoading(false));
    },

    sync: async (...items) => {
      return params.dataLayer.sync(...items).then(params.reload);
    },
  };
}
